import { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Phone, CheckCircle2, Clock, Calendar, ShieldCheck } from "lucide-react";
import { SITE } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = {
  name: "",
  phone: "",
  email: "",
  preferred_day: "Flexible",
  preferred_time: "Anytime",
  location: "",
  notes: "",
  campaign_code: "",
  response_code: "",
};

const DAYS = ["Today", "Tomorrow", "This week", "Next week", "Flexible"];
const TIMES = ["Morning (9–12)", "Afternoon (12–5)", "Evening (5–7)", "Anytime"];

export default function BookCall() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Pre-fill from QR/letter deep-link: /book-a-call?code=USL-XXXX&c=CAMP-YYYY
  useEffect(() => {
    const code = (params.get("code") || "").toUpperCase().trim();
    const camp = (params.get("c") || "").toUpperCase().trim();
    if (code || camp) {
      setForm(f => ({ ...f, response_code: code, campaign_code: camp }));
    }
  }, [params]);

  const tagged = !!(form.response_code || form.campaign_code);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Your name and phone number are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/callback-requests`, form);
      setDone(true);
      toast.success("Callback requested. We'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please call us on " + SITE.phone + ".");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="book-call-page" className="bg-[#0B0F0E]">
      <Toaster position="top-center" richColors theme="dark" />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-12">
        <div className="overline"><span className="h-line" />Request a callback</div>
        <h1 className="font-display text-[#F4EFE6] text-6xl md:text-7xl lg:text-[5.5rem] mt-7 leading-[0.95] max-w-4xl">
          Book a <span className="italic-gold">conversation</span><br />with our team.
        </h1>
        <p className="text-[#9BA39B] text-lg max-w-3xl mt-9 leading-relaxed">
          Tell us when suits you. A senior member of our team will call you back at your preferred time — confidential, no obligation, and no cost.
        </p>
        {tagged && (
          <div data-testid="callback-letter-tag" className="mt-8 inline-flex items-center gap-3 border border-[#C9A24B]/40 bg-[#C9A24B]/5 px-5 py-3">
            <CheckCircle2 size={16} className="text-[#C9A24B]" />
            <span className="text-[12px] tracking-[0.18em] uppercase text-[#C9A24B] font-semibold">
              Letter Reference {form.response_code || form.campaign_code}
            </span>
            <span className="text-[12px] text-[#9BA39B]">— we&apos;ll match this to your letter automatically.</span>
          </div>
        )}
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="callback-success" className="bg-[#C9A24B] text-[#0B0F0E] p-10 md:p-14">
              <CheckCircle2 size={42} />
              <h2 className="font-display text-5xl mt-6 leading-[1]">Callback requested.</h2>
              <p className="mt-5 text-[15px] leading-relaxed max-w-lg">
                Thank you, {(form.name || "").trim().split(" ")[0]}. We&apos;ll call you on <strong>{form.phone}</strong> within your preferred window. If you&apos;d like to speak sooner, we&apos;re on <a href={`tel:${SITE.phone.replace(/\s+/g, "")}`} className="underline font-semibold">{SITE.phone}</a>.
              </p>
              <button data-testid="callback-reset" onClick={() => { setDone(false); setForm(initial); }} className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold border border-[#0B0F0E] px-6 py-3 hover:bg-[#0B0F0E] hover:text-[#C9A24B] transition-colors">
                Request Another
              </button>
            </div>
          ) : (
            <form data-testid="callback-form" onSubmit={submit} className="bg-[#11171A] border border-[#F4EFE6]/10 p-8 md:p-12 space-y-7">
              <div className="grid md:grid-cols-2 gap-7">
                <div>
                  <label className="overline block mb-2">Your name *</label>
                  <input data-testid="callback-name" className="underline-input" value={form.name} onChange={onChange("name")} />
                </div>
                <div>
                  <label className="overline block mb-2">Phone number *</label>
                  <input data-testid="callback-phone" type="tel" className="underline-input" value={form.phone} onChange={onChange("phone")} placeholder="e.g. 07700 900000" />
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Email (optional)</label>
                <input data-testid="callback-email" type="email" className="underline-input" value={form.email} onChange={onChange("email")} />
              </div>

              <div className="border-t border-[#F4EFE6]/10 pt-7 grid md:grid-cols-2 gap-7">
                <div>
                  <label className="overline block mb-2 flex items-center gap-2"><Calendar size={12} /> Preferred day</label>
                  <select data-testid="callback-day" className="underline-input" value={form.preferred_day} onChange={onChange("preferred_day")}>
                    {DAYS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2 flex items-center gap-2"><Clock size={12} /> Preferred time</label>
                  <select data-testid="callback-time" className="underline-input" value={form.preferred_time} onChange={onChange("preferred_time")}>
                    {TIMES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Site postcode or area (optional)</label>
                <input data-testid="callback-location" className="underline-input uppercase tracking-[0.15em]" value={form.location} onChange={onChange("location")} placeholder="e.g. GL54" />
              </div>

              <div>
                <label className="overline block mb-2">Anything you&apos;d like us to know?</label>
                <textarea data-testid="callback-notes" rows={4} className="underline-input resize-none" value={form.notes} onChange={onChange("notes")} placeholder="Approximate acreage, planning status, timeline, or questions you'd like answered on the call." />
              </div>

              <button data-testid="callback-submit" type="submit" disabled={loading} className="usl-btn">
                {loading ? "Sending..." : "Request My Callback"} <Phone size={16} />
              </button>
              <p className="text-xs text-[#9BA39B]">Confidential. We use your details only to call you back. We never sell or share your data.</p>
            </form>
          )}
        </div>

        <aside className="lg:col-span-5">
          <div className="border border-[#F4EFE6]/10 bg-[#11171A] p-8 md:p-10">
            <div className="overline">What to expect</div>
            <ul className="mt-6 space-y-4 text-[15px] text-[#E8E1D2] leading-relaxed">
              <li className="flex gap-3"><span className="text-[#C9A24B]">—</span><span>A direct call from a senior planner — no call centres.</span></li>
              <li className="flex gap-3"><span className="text-[#C9A24B]">—</span><span>15-minute conversation at the time you choose.</span></li>
              <li className="flex gap-3"><span className="text-[#C9A24B]">—</span><span>Honest, plain-English answers about your land&apos;s potential.</span></li>
              <li className="flex gap-3"><span className="text-[#C9A24B]">—</span><span>If we proceed: promotion entirely at our cost.</span></li>
            </ul>
            <div className="border-t border-[#F4EFE6]/10 mt-8 pt-6 flex items-start gap-3 text-[12px] text-[#9BA39B]">
              <ShieldCheck size={16} className="text-[#C9A24B] mt-0.5 flex-shrink-0" />
              <span>All conversations are confidential and non-binding. You are under no obligation to proceed.</span>
            </div>
          </div>

          <a href={`tel:${SITE.phone.replace(/\s+/g, "")}`} data-testid="callback-direct-phone" className="mt-6 block border border-[#C9A24B]/40 bg-[#C9A24B]/5 p-6 hover:bg-[#C9A24B]/10 transition-colors">
            <div className="overline text-[#C9A24B]"><span className="h-line bg-[#C9A24B]" />Or call us now</div>
            <div className="font-display text-3xl text-[#F4EFE6] mt-3">{SITE.phone}</div>
            <div className="text-[12px] text-[#9BA39B] mt-2">Office hours · Mon–Fri 9:00–17:30</div>
          </a>
        </aside>
      </section>
    </div>
  );
}
